import axios from "axios";
import { attachAuthRefresh } from "../utils/tokenRefresh"; // apna actual path daalo

// The pooja app lives under the same host as the kundli app, one level up:
// .../api/kundli  ->  .../api/pooja
// Matches pooja/urls.py exactly:
//   GET  /api/pooja/                              -> listPoojas
//   GET  /api/pooja/<slug>/                       -> getPoojaDetail
//   GET  /api/pooja/<slug>/astrologers/           -> getPoojaAstrologers
//   POST /api/pooja/<slug>/initiate-booking/      -> initiateBooking
//   POST /api/pooja/<slug>/book/                  -> bookPooja
//   GET  /api/pooja/bookings/                     -> getUserBookings
//   GET  /api/pooja/bookings/<id>/                -> getBookingDetail
//   PUT  /api/pooja/bookings/<id>/change-astrologer/ -> changeAstrologer
//   POST /api/pooja/bookings/<id>/review/         -> submitReview
const KUNDLI_BASE = process.env.REACT_APP_API_BASE_URL || "";
const POOJA_BASE = KUNDLI_BASE.replace(/\/api\/kundli\/?$/, "/api/pooja");

export const poojaApi = axios.create({
  baseURL: POOJA_BASE,
  headers: { "Content-Type": "application/json" },
});

// Same auth pattern as kundliApi.js / walletApi.js.
poojaApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("kc_access_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handles expired/invalid access tokens automatically: refreshes silently,
// and if there's no valid refresh token either (anonymous visitor / stale
// token), retries once with no Authorization header at all — so AllowAny
// endpoints like listPoojas() still succeed instead of 401ing.
attachAuthRefresh(poojaApi);

const unwrap = (promise) =>
  promise
    .then((res) => res.data)
    .catch((err) => {
      const body = err.response?.data;
      const message = body?.message || body?.detail || "Something went wrong.";
      throw { message, errors: body?.errors, status: err.response?.status };
    });

// GET /api/pooja/ -> { success, count, data: [{ id, title, slug, price, original_price, discount_percent, thumbnail_url }] }
export const listPoojas = () => unwrap(poojaApi.get("/"));

// GET /api/pooja/<slug>/ -> full detail: images, categories, benefits, reviews,
// rating_breakdown, default_astrologer, gst/pricing fields
export const getPoojaDetail = (slug) => unwrap(poojaApi.get(`/${slug}/`));

// GET /api/pooja/<slug>/astrologers/ -> list of astrologers for the "Change" picker
export const getPoojaAstrologers = (slug) => unwrap(poojaApi.get(`/${slug}/astrologers/`));

// POST /api/pooja/<slug>/initiate-booking/
// payload: { astrologer_id?, coupon_code?, use_wallet?, billing_state? }
// -> price/GST/coupon/wallet breakdown (no booking created yet — just the math)
export const initiateBooking = (slug, payload) =>
  unwrap(poojaApi.post(`/${slug}/initiate-booking/`, payload));

// POST /api/pooja/<slug>/book/
// Same payload shape as initiateBooking. Creates the actual PoojaBooking row
// (+ a Razorpay order if anything is still owed after wallet/coupon).
// -> { booking_id, status } if fully wallet-paid (already confirmed)
// -> { booking_id, order_id, amount, currency, key_id, user_phone } if payment still due
export const bookPooja = (slug, payload) => unwrap(poojaApi.post(`/${slug}/book/`, payload));

// GET /api/pooja/bookings/ -> user's booking history
export const getUserBookings = () => unwrap(poojaApi.get("/bookings/"));

// GET /api/pooja/bookings/<id>/ -> single booking (used to check status after payment)
export const getBookingDetail = (id) => unwrap(poojaApi.get(`/bookings/${id}/`));

// PUT /api/pooja/bookings/<id>/change-astrologer/  { astrologer_id }
export const changeAstrologer = (id, astrologerId) =>
  unwrap(poojaApi.put(`/bookings/${id}/change-astrologer/`, { astrologer_id: astrologerId }));

// GET /api/pooja/bookings/<id>/join/<token>/  (public — no auth required, token is the access control)
// -> { booking_id, pooja_title, meeting_link, client_phone, astrologer_phone, status }
// Used by the WhatsApp video-call link (PoojaMeetingJoin.jsx reads pk/token from the URL itself).
export const getMeetingJoinInfo = (bookingId, token) =>
  unwrap(poojaApi.get(`/bookings/${bookingId}/join/${token}/`));

// POST /api/pooja/bookings/<id>/review/  { rating, comment }
export const submitReview = (id, payload) =>
  unwrap(poojaApi.post(`/bookings/${id}/review/`, payload));